import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ProjectContext = createContext();

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProjects must be inside ProjectProvider');
  return context;
};

export const ProjectProvider = ({ children, token }) => {
  // Silent access-token refresh — see useAuthRefresh for the rationale.
  useAuthRefresh(token, { backendUrl: BACKEND_URL, apiPrefix: '/api/projects' });

  const [projects, setProjects] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProjects = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page);
      if (filters.page_size) params.append('page_size', filters.page_size);
      const response = await axios.get(`${BACKEND_URL}/api/projects/list?${params}`, { headers });
      setProjects(response.data.projects || []);
      setTotalProjects(response.data.total || 0);
      return response.data;
    } catch (err) {
      console.error('fetchProjects error:', err);
      return { projects: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getProject = useCallback(async (id) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/detail/${id}`, { headers });
      return response.data;
    } catch (err) {
      return null;
    }
  }, [token]);

  const createProject = useCallback(async (data) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/projects/create`, data, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create' };
    }
  }, [token]);

  const updateProject = useCallback(async (id, data) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/projects/update/${id}`, data, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update' };
    }
  }, [token]);

  const deleteProject = useCallback(async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/delete/${id}`, { headers });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete' };
    }
  }, [token]);

  // Activity / Comments
  const getActivity = useCallback(async (projectId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/activity/${projectId}`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  const addComment = useCallback(async (projectId, comment) => {
    try {
      await axios.post(`${BACKEND_URL}/api/projects/comment/${projectId}`, { comment }, { headers });
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  }, [token]);

  // Attachments
  const getAttachments = useCallback(async (projectId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/attachments/${projectId}`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  const uploadAttachment = useCallback(async (projectId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${BACKEND_URL}/api/projects/attachments/${projectId}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Upload failed' };
    }
  }, [token]);

  const deleteAttachment = useCallback(async (projectId, attachmentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/attachments/${projectId}/${attachmentId}`, { headers });
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  }, [token]);

  const getAttachmentUrl = (projectId, attachmentId) => {
    return `${BACKEND_URL}/api/projects/attachments/${projectId}/download/${attachmentId}?token=${token}`;
  };

  // Dashboard & Analytics
  const getDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/dashboard-stats`, { headers });
      return response.data;
    } catch (err) {
      return null;
    }
  }, [token]);

  const getAnalytics = useCallback(async (fromDate, toDate) => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const response = await axios.get(`${BACKEND_URL}/api/projects/analytics?${params}`, { headers });
      return response.data;
    } catch (err) {
      return null;
    }
  }, [token]);

  // Tags
  const getTags = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/tags`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  // Settings
  const getSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/settings`, { headers });
      return response.data;
    } catch (err) {
      return null;
    }
  }, [token]);

  const updatePipelineStages = useCallback(async (stages) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/projects/settings/pipeline-stages`, { stages }, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update' };
    }
  }, [token]);

  const updatePlatforms = useCallback(async (platforms) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/projects/settings/platforms`, { platforms }, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update' };
    }
  }, [token]);

  const updateDefaultMembers = useCallback(async (members) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/projects/settings/default-members`, { members }, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update' };
    }
  }, [token]);

  // Users lookup
  const getUsersLookup = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/users-lookup`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  return (
    <ProjectContext.Provider value={{
      projects, totalProjects, loading,
      fetchProjects, getProject, createProject, updateProject, deleteProject,
      getActivity, addComment,
      getAttachments, uploadAttachment, deleteAttachment, getAttachmentUrl,
      getDashboardStats, getAnalytics,
      getTags, getSettings,
      updatePipelineStages, updatePlatforms, updateDefaultMembers,
      getUsersLookup
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;
